import type { JSX } from "react";
import { useCallback } from "react";
import { CharityInput } from "./CharityInput";
import { EmployerPensionInput } from "./EmployerPensionInput";
import { PensionInput } from "./PensionInput";
import { CollapsibleSection, FormFieldSet, type FieldConfig } from "./ui";
import { validateTaxCode } from "../utils/taxCodeValidation";
import type { InputMode } from "../hooks/useAmountPercentageToggle";
import type { PersonInput, PensionType, CharityType } from "../types";

/**
 * Validates tax code and returns error message if invalid
 */
const validateTaxCodeField = (value: string | number): string | undefined => {
  const result = validateTaxCode(String(value));
  return result.valid ? undefined : result.error;
};

/**
 * Field preset configurations for different person form modes
 */
export type PersonFormMode = 'basic' | 'full' | 'income-only';

const BASIC_FIELDS: Array<FieldConfig<PersonInput>> = [
  { label: "Gross Annual Salary", key: "gross_salary", type: "number", prefix: "£" },
  { label: "Tax Code", key: "tax_code", type: "text", validate: validateTaxCodeField },
];

const INCOME_ONLY_FIELDS: Array<FieldConfig<PersonInput>> = [
  { label: "Gross Annual Salary", key: "gross_salary", type: "number", prefix: "£" },
];

const FIELD_PRESETS: Record<PersonFormMode, Array<FieldConfig<PersonInput>>> = {
  basic: BASIC_FIELDS,
  full: [],  // Handled separately in component
  'income-only': INCOME_ONLY_FIELDS,
};

export interface PersonFormSectionProps<T extends PersonInput> {
  /**
   * Field configuration mode
   */
  mode: PersonFormMode;

  /**
   * Form data
   */
  data: T;

  /**
   * State setter function - receives the complete updated state
   */
  setData: (data: T) => void;

  /**
   * Optional section title
   */
  title?: string;
}

/**
 * PersonFormSection - unified component for person input forms
 *
 * @example
 * ```tsx
 * <PersonFormSection
 *   mode="full"
 *   data={personData}
 *   setData={setPersonData}
 *   title="Person 1"
 * />
 * ```
 */
export function PersonFormSection<T extends PersonInput>({
  mode,
  data,
  setData,
  title,
}: PersonFormSectionProps<T>): JSX.Element {
  // Helper to handle single field changes
  const handleSingleFieldChange = useCallback((field: keyof PersonInput, value: number | string): void => {
    setData({ ...data, [field]: value } as T);
  }, [data, setData]);

  const handlePensionChange = useCallback((value: number, valueType: InputMode): void => {
    // Update both the value field and type field in a single call
    const updates: Partial<PersonInput> = {
      net_pension_contributions_type: valueType,
    };
    if (valueType === 'percentage') {
      updates.net_pension_contributions_percentage = value;
    } else {
      updates.net_pension_contributions_amount = value;
    }
    setData({ ...data, ...updates } as T);
  }, [data, setData]);

  const handlePensionTypeChange = useCallback((type: PensionType): void => {
    setData({ ...data, pension_type: type } as T);
  }, [data, setData]);

  const handleEmployerPensionChange = useCallback((value: number, valueType: InputMode): void => {
    // Update both the value field and type field in a single call
    const updates: Partial<PersonInput> = {
      employer_pension_contributions_type: valueType,
    };
    if (valueType === 'percentage') {
      updates.employer_pension_contributions_percentage = value;
    } else {
      updates.employer_pension_contributions_amount = value;
    }
    setData({ ...data, ...updates } as T);
  }, [data, setData]);

  const handleCharityChange = useCallback((value: number): void => {
    // Update the appropriate field based on charity type
    const field = data.charity_type === 'payroll_giving' ? 'payroll_giving_donations' : 'gift_aid_donations';
    setData({ ...data, [field]: value } as T);
  }, [data, setData]);

  const handleCharityTypeChange = useCallback((type: CharityType): void => {
    setData({ ...data, charity_type: type } as T);
  }, [data, setData]);

  if (mode === 'full') {
    return (
      <section>
        {title && <h2 className="text-lg font-semibold mb-4">{title}</h2>}
        <div className="space-y-6">
          {/* Income Section */}
          <div className="space-y-4">
            <FormFieldSet<PersonInput>
              fields={[
                {
                  label: "Gross Annual Salary",
                  key: "gross_salary",
                  type: "number",
                  prefix: "£",
                  helperText: "Your total annual salary before any deductions"
                },
                {
                  label: "Annual Bonus",
                  key: "bonus",
                  type: "number",
                  prefix: "£",
                  defaultValue: 0,
                  helperText: "Expected annual bonus (taxed as regular income)"
                },
                {
                  label: "Tax Code",
                  key: "tax_code",
                  type: "text",
                  helperText: "Your tax code (e.g. 1257L, BR, 0T)",
                  validate: validateTaxCodeField
                },
              ]}
              data={data}
              onChange={handleSingleFieldChange}
            />
          </div>

          {/* Pension Section */}
          <CollapsibleSection title="Pension" defaultExpanded={true}>
            <div className="space-y-4">
              <PensionInput
                percentageValue={data.net_pension_contributions_percentage ?? 0}
                amountValue={data.net_pension_contributions_amount ?? 0}
                activeMode={data.net_pension_contributions_type || 'percentage'}
                onChange={handlePensionChange}
                pensionType={data.pension_type || 'salary_sacrifice'}
                onPensionTypeChange={handlePensionTypeChange}
              />
              <EmployerPensionInput
                percentageValue={data.employer_pension_contributions_percentage ?? 0}
                amountValue={data.employer_pension_contributions_amount ?? 0}
                activeMode={data.employer_pension_contributions_type || 'percentage'}
                onChange={handleEmployerPensionChange}
              />
            </div>
          </CollapsibleSection>

          {/* Charity Section */}
          <CollapsibleSection title="Charity" defaultExpanded={false}>
            <CharityInput
              value={data.charity_type === 'payroll_giving' ? data.payroll_giving_donations : data.gift_aid_donations}
              onChange={handleCharityChange}
              charityType={data.charity_type || 'gift_aid'}
              onCharityTypeChange={handleCharityTypeChange}
            />
          </CollapsibleSection>

          {/* Investment Income Section */}
          <CollapsibleSection title="Investment Income" defaultExpanded={false}>
            <div className="space-y-4">
              <FormFieldSet<PersonInput>
                fields={[
                  {
                    label: "Dividend Income",
                    key: "dividend_income",
                    type: "number",
                    prefix: "£",
                    defaultValue: 0,
                    helperText: "Annual dividends from stocks and shares. £500 tax-free allowance, then 8.75%/33.75%/39.35%"
                  },
                  {
                    label: "Interest Income",
                    key: "interest_income",
                    type: "number",
                    prefix: "£",
                    defaultValue: 0,
                    helperText: "Annual interest from savings. Personal Savings Allowance: £1k/£500/£0 based on tax band"
                  },
                ]}
                data={data}
                onChange={handleSingleFieldChange}
              />
            </div>
          </CollapsibleSection>

          {/* Salary Sacrifice & Benefits Section */}
          <CollapsibleSection title="Other Deductions & Benefits" defaultExpanded={false}>
            <div className="space-y-4">
              <FormFieldSet<PersonInput>
                fields={[
                  {
                    label: "Taxable Benefits",
                    key: "taxable_benefits",
                    type: "number",
                    prefix: "£",
                    helperText: "Value of benefits like company car, health insurance, or other perks taxed as income"
                  },
                  {
                    label: "Salary Sacrifice",
                    key: "salary_sacrifice_contributions",
                    type: "number",
                    prefix: "£",
                    helperText: "Other salary sacrifice schemes like electric vehicle, cycle to work, etc."
                  },
                ]}
                data={data}
                onChange={handleSingleFieldChange}
              />
            </div>
          </CollapsibleSection>
        </div>
      </section>
    );
  }

  const fields = FIELD_PRESETS[mode];

  return (
    <section>
      {title && <h2 className="text-lg font-semibold mb-4">{title}</h2>}
      <FormFieldSet<PersonInput> fields={fields} data={data} onChange={handleSingleFieldChange} />
    </section>
  );
}
